import { useState } from "react";
import { UserButton, OrganizationSwitcher } from "@clerk/clerk-react";
import { ChatPanel } from "./chat/ChatPanel.js";
import { AnalyticsDashboard } from "./dashboard/AnalyticsDashboard.js";
import { SkillsBrowser } from "./skills/SkillsBrowser.js";
import { HooksConfigurator } from "./hooks/HooksConfigurator.js";
import { IntegrationsPanel } from "./integrations/IntegrationsPanel.js";
import { SettingsPanel } from "./settings/SettingsPanel.js";
import { ProjectsView } from "./projects/ProjectsView.js";
import { cn } from "../lib/utils.js";

const AUTH_ENABLED = Boolean(import.meta.env["VITE_CLERK_PUBLISHABLE_KEY"]);

type Tab = "chat" | "projects" | "analytics" | "skills" | "integrations" | "hooks";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  {
    id: "chat", label: "Chat",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 10.5a1.5 1.5 0 0 1-1.5 1.5H5l-3 3V2.5A1.5 1.5 0 0 1 3.5 1h9A1.5 1.5 0 0 1 14 2.5v8z" />
      </svg>
    ),
  },
  {
    id: "projects", label: "Projects",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 4a1 1 0 0 1 1-1h4l2 2h6a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4z" />
      </svg>
    ),
  },
  {
    id: "analytics", label: "Analytics",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12l4-4 3 3 4-5 3 3" />
      </svg>
    ),
  },
  {
    id: "skills", label: "Skills",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="8,1 10,6 15,6 11,9.5 12.5,14.5 8,11.5 3.5,14.5 5,9.5 1,6 6,6" />
      </svg>
    ),
  },
  {
    id: "integrations", label: "Integrations",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="1" width="5" height="5" rx="1" />
        <rect x="10" y="1" width="5" height="5" rx="1" />
        <rect x="1" y="10" width="5" height="5" rx="1" />
        <path d="M13.5 10v2m0 3v-1M13.5 12h-2m5 0h-1" />
      </svg>
    ),
  },
  {
    id: "hooks", label: "Hooks",
    icon: (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8 1v8a3 3 0 1 1-3-3" />
      </svg>
    ),
  },
];

export function Layout() {
  const [activeTab, setActiveTab] = useState<Tab>("chat");
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div className="flex h-screen" style={{ background: "var(--bg-primary)" }}>
      {/* Left nav rail */}
      <div
        className="w-[52px] flex flex-col items-center py-3 gap-0.5 flex-shrink-0"
        style={{ borderRight: "1px solid var(--border)", background: "var(--bg-secondary)" }}
      >
        {/* CoWork logo mark */}
        <div className="w-8 h-8 rounded-[10px] flex items-center justify-center mb-4 flex-shrink-0"
          style={{ background: "var(--accent)" }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2C4.69 2 2 4.69 2 8s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10.5A4.5 4.5 0 1 1 8 3.5a4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z" fill="white" />
          </svg>
        </div>

        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            title={tab.label}
            className={cn(
              "w-9 h-9 rounded-lg flex items-center justify-center transition-colors",
              activeTab === tab.id
                ? "text-[var(--accent)]"
                : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]",
            )}
            style={activeTab === tab.id ? { background: "var(--accent-muted)" } : {}}
          >
            {tab.icon}
          </button>
        ))}

        <div className="flex-1" />

        {AUTH_ENABLED && (
          <>
            <div className="w-9 flex justify-center mb-0.5">
              <OrganizationSwitcher
                hidePersonal
                appearance={{
                  elements: {
                    rootBox: "w-full",
                    organizationSwitcherTrigger: "w-9 h-9 rounded-lg flex items-center justify-center p-0",
                  },
                }}
              />
            </div>
            <div className="w-9 flex justify-center mb-0.5">
              <UserButton appearance={{ elements: { avatarBox: "w-7 h-7" } }} />
            </div>
          </>
        )}

        <button
          onClick={() => setShowSettings(true)}
          title="Settings"
          className="w-9 h-9 rounded-lg flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="8" cy="8" r="2.5" />
            <path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.05 3.05l1.06 1.06M11.89 11.89l1.06 1.06M3.05 12.95l1.06-1.06M11.89 4.11l1.06-1.06" />
          </svg>
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {activeTab === "chat"         && <ChatPanel />}
        {activeTab === "projects"     && <ProjectsView />}
        {activeTab === "analytics"    && <AnalyticsDashboard />}
        {activeTab === "skills"       && <SkillsBrowser />}
        {activeTab === "integrations" && <IntegrationsPanel />}
        {activeTab === "hooks"        && <HooksConfigurator />}
      </div>

      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} />}
    </div>
  );
}
